import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Home, Loader2, CheckCircle2, Circle, Clock, ShieldCheck, FileText, CreditCard,
  DoorOpen, ExternalLink, AlertTriangle, ArrowRight, Sparkles, MapPin, CalendarDays, Users,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UI = {
  it: {
    welcome: "Benvenuto", subtitle: "Il tuo check-in digitale", loading: "Caricamento del tuo portale…",
    errTitle: "Portale non disponibile", errSub: "Contatta la struttura per assistenza.",
    countdown: "Mancano al tuo arrivo", days: "giorni", hours: "ore", mins: "min", arrived: "Il tuo soggiorno è iniziato",
    yourStay: "Il tuo soggiorno", checkin: "Check-in", checkout: "Check-out", guests: "Ospiti",
    progress: "Stato della tua pratica",
    termsTitle: "Condizioni di soggiorno", termsSub: "Per procedere con il check-in, leggi e accetta:",
    accTerms: "Accetto i Termini e Condizioni", accPrivacy: "Accetto l'Informativa sulla Privacy",
    accRules: "Accetto il Regolamento della casa", read: "Leggi", accept: "Accetta e continua", accepting: "Salvataggio…",
    checkinTitle: "Completa il check-in", checkinSub: "Compila il modulo con i dati dei documenti. Resterai su Urban OS.",
    guestsQ: "Quanti ospiti?", guestsHint: "Seleziona il numero di ospiti per il tuo soggiorno.", proceed: "Prosegui", changeGuests: "Modifica numero ospiti", guestsSaved: "Ospiti selezionati",
    openForm: "Apri il modulo di check-in", newTab: "Il modulo non si carica? Aprilo in una nuova scheda",
    embedNote: "Puoi compilare il modulo qui sotto. Se non appare (blocco iframe/cookie), usa il pulsante qui sopra.",
    done: "Ho completato il check-in", refreshing: "Aggiorno lo stato…",
    reviewTitle: "Documenti in verifica", reviewSub: "I tuoi documenti sono stati ricevuti. Il nostro team li sta verificando.",
    changesTitle: "Servono integrazioni", changesSub: "Il team ha richiesto una modifica ai documenti. Riapri il modulo per completarli.",
    taxTitle: "Tassa di soggiorno", taxSub: "Puoi pagare ora, mentre verifichiamo i documenti.",
    taxCalc: "Calcolo della tassa di soggiorno in corso…", taxAmount: "Importo dovuto", taxPay: "Paga la tassa di soggiorno",
    taxPaying: "Reindirizzamento al pagamento…", taxPending: "Pagamento in elaborazione…", taxPaid: "Tassa di soggiorno pagata",
    taxExempt: "Esente dalla tassa di soggiorno", taxNotDue: "Nessuna tassa dovuta", taxElsewhere: "Tassa già regolarizzata",
    taxFailed: "Pagamento non riuscito. Riprova.", taxReceipt: "Ricevuta",
    approvedTitle: "Check-in approvato", approvedSub: "Tutto pronto! Presto riceverai le istruzioni di accesso.",
    accessTitle: "Accesso disponibile", accessSub: "Il tuo Welcome Center è attivo con QR, Wi-Fi e istruzioni.",
    openWelcome: "Apri il tuo Welcome Center", cancelled: "Prenotazione annullata",
    footer: "Powered by Urban OS · Urban Dreams Home",
    receivedTitle: "Check-in ricevuto correttamente ✅", receivedSub: "Abbiamo ricevuto i dati richiesti e, ove previsto, il pagamento della tassa di soggiorno. Il nostro staff procederà alla verifica. Le istruzioni di accesso verranno inviate separatamente dopo la verifica.",
    steps: { prenotazione: "Prenotazione confermata", condizioni: "Condizioni accettate", ospiti: "Numero ospiti", documenti: "Documenti inviati", tassa: "Tassa di soggiorno", ricevuto: "Check-in ricevuto", verifica: "Verifica operatore", istruzioni: "Istruzioni di accesso inviate", approvato: "Check-in approvato", accesso: "Accesso disponibile" },
  },
  en: {
    welcome: "Welcome", subtitle: "Your digital check-in", loading: "Loading your portal…",
    errTitle: "Portal unavailable", errSub: "Please contact the property for assistance.",
    countdown: "Until your arrival", days: "days", hours: "hours", mins: "min", arrived: "Your stay has started",
    yourStay: "Your stay", checkin: "Check-in", checkout: "Check-out", guests: "Guests",
    progress: "Your check-in status",
    termsTitle: "Stay conditions", termsSub: "To proceed with check-in, please read and accept:",
    accTerms: "I accept the Terms & Conditions", accPrivacy: "I accept the Privacy Policy",
    accRules: "I accept the House Rules", read: "Read", accept: "Accept & continue", accepting: "Saving…",
    checkinTitle: "Complete your check-in", checkinSub: "Fill in the form with your document details. You'll stay on Urban OS.",
    guestsQ: "How many guests?", guestsHint: "Select the number of guests for your stay.", proceed: "Continue", changeGuests: "Change number of guests", guestsSaved: "Guests selected",
    openForm: "Open the check-in form", newTab: "Form not loading? Open it in a new tab",
    embedNote: "You can fill the form below. If it doesn't appear (iframe/cookie blocked), use the button above.",
    done: "I've completed the check-in", refreshing: "Refreshing status…",
    reviewTitle: "Documents under review", reviewSub: "Your documents were received. Our team is reviewing them.",
    changesTitle: "Additional info needed", changesSub: "The team requested a change to your documents. Reopen the form to complete them.",
    taxTitle: "City tax", taxSub: "You can pay now, while we review your documents.",
    taxCalc: "Calculating the city tax…", taxAmount: "Amount due", taxPay: "Pay the city tax",
    taxPaying: "Redirecting to payment…", taxPending: "Payment processing…", taxPaid: "City tax paid",
    taxExempt: "Exempt from city tax", taxNotDue: "No tax due", taxElsewhere: "Tax already settled",
    taxFailed: "Payment failed. Please try again.", taxReceipt: "Receipt",
    approvedTitle: "Check-in approved", approvedSub: "All set! You'll soon receive your access instructions.",
    accessTitle: "Access available", accessSub: "Your Welcome Center is active with QR, Wi-Fi and instructions.",
    openWelcome: "Open your Welcome Center", cancelled: "Booking cancelled",
    footer: "Powered by Urban OS · Urban Dreams Home",
    receivedTitle: "Check-in received ✅", receivedSub: "We've received your details and, where applicable, the city tax payment. Our staff will verify them. Access instructions will be sent separately after verification.",
    steps: { prenotazione: "Booking confirmed", condizioni: "Conditions accepted", ospiti: "Number of guests", documenti: "Documents submitted", tassa: "City tax", ricevuto: "Check-in received", verifica: "Operator verification", istruzioni: "Access instructions sent", approvato: "Check-in approved", accesso: "Access available" },
  },
  es: {
    welcome: "Bienvenido", subtitle: "Tu check-in digital", loading: "Cargando tu portal…",
    errTitle: "Portal no disponible", errSub: "Contacta con el alojamiento para asistencia.",
    countdown: "Para tu llegada", days: "días", hours: "horas", mins: "min", arrived: "Tu estancia ha comenzado",
    yourStay: "Tu estancia", checkin: "Check-in", checkout: "Check-out", guests: "Huéspedes",
    progress: "Estado de tu check-in",
    termsTitle: "Condiciones de estancia", termsSub: "Para continuar con el check-in, lee y acepta:",
    accTerms: "Acepto los Términos y Condiciones", accPrivacy: "Acepto la Política de Privacidad",
    accRules: "Acepto las Normas de la casa", read: "Leer", accept: "Aceptar y continuar", accepting: "Guardando…",
    checkinTitle: "Completa tu check-in", checkinSub: "Rellena el formulario con los datos de tus documentos. Permanecerás en Urban OS.",
    guestsQ: "¿Cuántos huéspedes?", guestsHint: "Selecciona el número de huéspedes de tu estancia.", proceed: "Continuar", changeGuests: "Cambiar número de huéspedes", guestsSaved: "Huéspedes seleccionados",
    openForm: "Abrir el formulario de check-in", newTab: "¿El formulario no carga? Ábrelo en una pestaña nueva",
    embedNote: "Puedes rellenar el formulario abajo. Si no aparece (iframe/cookies bloqueados), usa el botón de arriba.",
    done: "He completado el check-in", refreshing: "Actualizando estado…",
    reviewTitle: "Documentos en revisión", reviewSub: "Hemos recibido tus documentos. Nuestro equipo los está revisando.",
    changesTitle: "Se necesita más información", changesSub: "El equipo ha solicitado un cambio en tus documentos. Reabre el formulario para completarlos.",
    taxTitle: "Tasa turística", taxSub: "Puedes pagar ahora, mientras revisamos tus documentos.",
    taxCalc: "Calculando la tasa turística…", taxAmount: "Importe a pagar", taxPay: "Pagar la tasa turística",
    taxPaying: "Redirigiendo al pago…", taxPending: "Procesando el pago…", taxPaid: "Tasa turística pagada",
    taxExempt: "Exento de la tasa turística", taxNotDue: "Sin tasa a pagar", taxElsewhere: "Tasa ya regularizada",
    taxFailed: "Pago fallido. Inténtalo de nuevo.", taxReceipt: "Recibo",
    approvedTitle: "Check-in aprobado", approvedSub: "¡Todo listo! Pronto recibirás las instrucciones de acceso.",
    accessTitle: "Acceso disponible", accessSub: "Tu Welcome Center está activo con QR, Wi-Fi e instrucciones.",
    openWelcome: "Abrir tu Welcome Center", cancelled: "Reserva cancelada",
    footer: "Powered by Urban OS · Urban Dreams Home",
    receivedTitle: "Check-in recibido correctamente ✅", receivedSub: "Hemos recibido tus datos y, cuando corresponda, el pago de la tasa turística. Nuestro equipo los verificará. Las instrucciones de acceso se enviarán por separado tras la verificación.",
    steps: { prenotazione: "Reserva confirmada", condizioni: "Condiciones aceptadas", ospiti: "Número de huéspedes", documenti: "Documentos enviados", tassa: "Tasa turística", ricevuto: "Check-in recibido", verifica: "Verificación del operador", istruzioni: "Instrucciones de acceso enviadas", approvato: "Check-in aprobado", accesso: "Acceso disponible" },
  },
};

const detectLang = () => {
  const q = new URLSearchParams(window.location.search).get("lang");
  if (q && UI[q]) return q;
  const saved = localStorage.getItem("udh_lang");
  if (saved && UI[saved]) return saved;
  const b = (navigator.language || "it").slice(0, 2).toLowerCase();
  return UI[b] ? b : "it";
};

const fmtDate = (v) => { try { return new Date(v).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }); } catch { return v; } };
const eur = (n) => `€ ${Number(n || 0).toFixed(2)}`;

const Card = ({ children, className = "", testid }) => (
  <div data-testid={testid} className={`rounded-3xl border border-white/10 bg-white/[0.04] p-5 backdrop-blur-xl ${className}`}>{children}</div>
);

const StepIcon = ({ state }) => {
  if (state === "done") return <CheckCircle2 size={20} className="text-emerald-400" />;
  if (state === "pending") return <Loader2 size={20} className="animate-spin text-amber-400" />;
  if (state === "blocked") return <AlertTriangle size={20} className="text-rose-400" />;
  return <Circle size={20} className="text-slate-600" />;
};

export default function GuestPortal() {
  const { code } = useParams();
  const [params, setParams] = useSearchParams();
  const [lang, setLang] = useState(detectLang());
  const t = UI[lang];
  const changeLang = (l) => { setLang(l); try { localStorage.setItem("udh_lang", l); } catch (_) {} };
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [accepting, setAccepting] = useState(false);
  const [acc, setAcc] = useState({ terms: false, privacy: false, rules: false });
  const [paying, setPaying] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [guestSel, setGuestSel] = useState(null);
  const [guestsConfirmed, setGuestsConfirmed] = useState(false);
  const [tallyUrl, setTallyUrl] = useState("");
  const [savingGuests, setSavingGuests] = useState(false);
  const [submittingDone, setSubmittingDone] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/public/portal/${code}`);
      setData(r.data); setErr("");
    } catch (e) {
      setErr(e.response?.data?.detail || "error");
    } finally { setLoading(false); }
  }, [code]);

  useEffect(() => { load(); }, [load]);

  // Init selettore ospiti UNA SOLA VOLTA (il polling non deve resettare la scelta dell'ospite)
  const guestsInitRef = useRef(false);
  useEffect(() => {
    if (!data || guestsInitRef.current) return;
    guestsInitRef.current = true;
    setTallyUrl(data.tally_url || "");
    setGuestSel(data.declared_guests || data.booking_guests || 1);
    setGuestsConfirmed(!!data.declared_guests);
  }, [data]);

  const saveGuests = async () => {
    if (!guestSel || savingGuests) return;
    setSavingGuests(true);
    try {
      const r = await axios.post(`${API}/public/portal/${code}/set-guests`, { guests: guestSel });
      setTallyUrl(r.data.tally_url || "");
      setGuestsConfirmed(true);
    } catch (e) { setErr(e.response?.data?.detail || "error"); }
    finally { setSavingGuests(false); }
  };

  const checkinDone = async () => {
    if (submittingDone) return;
    setSubmittingDone(true);
    try { await axios.post(`${API}/public/portal/${code}/checkin-done`); await load(); }
    catch (e) { setErr(e.response?.data?.detail || "error"); }
    finally { setSubmittingDone(false); }
  };

  // Sicurezza: pagina ospite non indicizzabile dai motori di ricerca
  useEffect(() => {
    const m = document.createElement("meta");
    m.name = "robots"; m.content = "noindex, nofollow";
    document.head.appendChild(m);
    return () => { document.head.removeChild(m); };
  }, []);

  // polling ogni 15s (in pausa quando la tab non è visibile) per riflettere gli aggiornamenti
  useEffect(() => {
    const tick = () => { if (document.visibilityState === "visible") load(); };
    pollRef.current = setInterval(tick, 15000);
    return () => clearInterval(pollRef.current);
  }, [load]);

  useEffect(() => { const i = setInterval(() => setNow(Date.now()), 60000); return () => clearInterval(i); }, []);

  // ritorno dal pagamento Stripe
  useEffect(() => {
    const sess = params.get("tax_session");
    if (!sess) return;
    let tries = 0;
    const check = async () => {
      try {
        const r = await axios.get(`${API}/public/portal/${code}/city-tax/status/${sess}`);
        if (r.data.payment_status === "paid") { setParams({}, { replace: true }); load(); return; }
        if (["expired", "failed", "canceled"].includes(r.data.status)) { setParams({}, { replace: true }); load(); return; }
      } catch { /* ignore */ }
      if (++tries < 6) setTimeout(check, 2500); else { setParams({}, { replace: true }); load(); }
    };
    check();
  }, [params, code, load, setParams]);

  const doAccept = async () => {
    setAccepting(true);
    try { await axios.post(`${API}/public/portal/${code}/accept`, acc); await load(); }
    catch (e) { setErr(e.response?.data?.detail || "error"); }
    finally { setAccepting(false); }
  };

  const payTax = async () => {
    setPaying(true);
    try {
      const r = await axios.post(`${API}/public/portal/${code}/city-tax/checkout`, { origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) { setErr(e.response?.data?.detail || "error"); setPaying(false); }
  };

  if (loading) return (
    <div className="flex min-h-screen items-center justify-center bg-[#0a1020] text-slate-300">
      <div className="text-center"><Loader2 size={34} className="mx-auto animate-spin text-indigo-400" /><p className="mt-3 text-sm">{t.loading}</p></div>
    </div>
  );

  if (err) return (
    <div className="flex min-h-screen items-center justify-center bg-[#0a1020] px-6 text-center text-slate-300">
      <div data-testid="portal-error"><AlertTriangle size={40} className="mx-auto text-rose-400" /><p className="mt-3 text-lg font-semibold">{t.errTitle}</p><p className="mt-1 text-sm text-slate-500">{typeof err === "string" && err !== "error" ? err : t.errSub}</p></div>
    </div>
  );

  const d = data;
  const ci = new Date(`${d.check_in}T${(d.checkin_time || "15:00")}:00`);
  const diff = ci.getTime() - now;
  const arrived = diff <= 0;
  const cd = { d: Math.floor(diff / 86400000), h: Math.floor((diff % 86400000) / 3600000), m: Math.floor((diff % 3600000) / 60000) };
  const tax = d.city_tax || {};
  const showCheckinForm = d.terms_accepted && ["not_started", "in_progress", "changes_requested", "rejected"].includes(d.document_state);
  const inReview = ["submitted", "under_review"].includes(d.document_state);
  const needChanges = ["changes_requested", "rejected"].includes(d.document_state);
  const showTax = d.terms_accepted && d.docs_submitted && !tax.regularized;
  const effTally = tallyUrl || d.tally_url || "";
  const tallySrc = effTally ? `${effTally}${effTally.includes("?") ? "&" : "?"}transparentBackground=1&hideTitle=1&lang=${lang}` : "";
  const guestOptions = d.guest_options || [];

  const taxBanner = () => {
    if (tax.regularized) {
      const label = tax.status === "exempt" ? t.taxExempt : tax.status === "not_due" ? t.taxNotDue : tax.status === "collected_elsewhere" ? t.taxElsewhere : t.taxPaid;
      return (
        <Card testid="portal-tax-paid" className="border-emerald-400/25 bg-emerald-500/10">
          <div className="flex items-center gap-3"><CheckCircle2 size={22} className="text-emerald-400" /><div><div className="font-semibold text-emerald-200">{label}</div>{tax.receipt?.amount ? <div className="text-xs text-emerald-300/70">{t.taxReceipt}: {eur(tax.receipt.amount)}</div> : null}</div></div>
        </Card>
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-[#0a1020] pb-14 text-slate-100" style={{ backgroundImage: "radial-gradient(60% 40% at 50% 0%, rgba(79,70,229,0.18), transparent)" }}>
      <div className="mx-auto max-w-md px-4">
        {/* Header */}
        <header className="flex items-center justify-between pt-6">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-indigo-300"><Home size={15} /> {d.brand?.name || "Urban Dreams Home"}</div>
          <div className="flex gap-1 rounded-full bg-white/5 p-1" data-testid="portal-lang">
            {Object.keys(UI).map((l) => (
              <button key={l} onClick={() => changeLang(l)} className={`rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase transition-colors ${lang === l ? "bg-white text-slate-900" : "text-slate-300 hover:bg-white/10"}`}>{l}</button>
            ))}
          </div>
        </header>

        {/* Hero */}
        <section className="mt-6" data-testid="portal-hero">
          <p className="text-sm font-medium text-indigo-300">{t.welcome}, {d.guest_name?.split(" ")[0] || ""}</p>
          <h1 className="mt-1 font-head text-3xl font-semibold leading-tight">{d.apartment?.name}</h1>
          {(d.apartment?.zone || d.apartment?.address) && <p className="mt-1 flex items-center gap-1.5 text-sm text-slate-400"><MapPin size={14} /> {d.apartment?.zone || d.apartment?.address}</p>}
          <p className="mt-1 text-xs text-slate-500">{t.subtitle}</p>
        </section>

        {d.pratica_status === "cancelled" ? (
          <Card testid="portal-cancelled" className="mt-6 border-rose-400/25 bg-rose-500/10 text-center">
            <AlertTriangle size={30} className="mx-auto text-rose-400" /><p className="mt-2 font-semibold text-rose-200">{t.cancelled}</p>
          </Card>
        ) : (
          <>
            {/* Countdown + soggiorno */}
            <Card testid="portal-countdown" className="mt-5">
              {!arrived ? (
                <>
                  <div className="text-xs font-medium uppercase tracking-wide text-slate-400"><Clock size={13} className="mr-1 inline" />{t.countdown}</div>
                  <div className="mt-2 flex items-end gap-4">
                    <div><span className="text-3xl font-bold text-indigo-300">{cd.d}</span> <span className="text-xs text-slate-400">{t.days}</span></div>
                    <div><span className="text-3xl font-bold text-indigo-300">{cd.h}</span> <span className="text-xs text-slate-400">{t.hours}</span></div>
                    <div><span className="text-3xl font-bold text-indigo-300">{cd.m}</span> <span className="text-xs text-slate-400">{t.mins}</span></div>
                  </div>
                </>
              ) : (
                <div className="flex items-center gap-2 text-emerald-300"><Sparkles size={18} /> <span className="font-semibold">{t.arrived}</span></div>
              )}
              <div className="mt-4 grid grid-cols-3 gap-2 border-t border-white/10 pt-3 text-center text-xs">
                <div><div className="text-slate-500">{t.checkin}</div><div className="mt-0.5 font-medium"><CalendarDays size={12} className="mr-1 inline text-indigo-300" />{fmtDate(d.check_in)}</div></div>
                <div><div className="text-slate-500">{t.checkout}</div><div className="mt-0.5 font-medium">{fmtDate(d.check_out)}</div></div>
                <div><div className="text-slate-500">{t.guests}</div><div className="mt-0.5 font-medium">{d.guests}</div></div>
              </div>
            </Card>

            {/* Checklist */}
            <Card testid="portal-checklist" className="mt-4">
              <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-400">{t.progress}</div>
              <div className="space-y-0.5">
                {d.checklist.map((s, i) => (
                  <div key={s.key} data-testid={`portal-step-${s.key}`} className="flex items-center gap-3 py-1.5">
                    <StepIcon state={s.state} />
                    <span className={`text-sm ${s.state === "done" ? "text-slate-200" : s.state === "todo" ? "text-slate-500" : "text-slate-100"}`}>{t.steps[s.key] || s.label}</span>
                  </div>
                ))}
              </div>
            </Card>

            {/* STEP: Condizioni */}
            {!d.terms_accepted && (
              <Card testid="portal-terms" className="mt-4 border-indigo-400/25">
                <div className="flex items-center gap-2 font-semibold text-indigo-200"><ShieldCheck size={18} /> {t.termsTitle}</div>
                <p className="mt-1 text-xs text-slate-400">{t.termsSub}</p>
                <div className="mt-3 space-y-2.5">
                  {[["terms", t.accTerms, d.terms?.terms_url], ["privacy", t.accPrivacy, d.terms?.privacy_url], ["rules", t.accRules, d.terms?.rules_url]].map(([k, label, url]) => (
                    <label key={k} className="flex items-start gap-2.5 text-sm">
                      <input data-testid={`portal-check-${k}`} type="checkbox" checked={acc[k]} onChange={(e) => setAcc((a) => ({ ...a, [k]: e.target.checked }))} className="mt-0.5 h-4 w-4 accent-indigo-500" />
                      <span className="flex-1 text-slate-200">{label} {url ? <a href={url} target="_blank" rel="noreferrer" className="ml-1 inline-flex items-center gap-0.5 text-xs text-indigo-300 underline">{t.read}<ExternalLink size={11} /></a> : null}</span>
                    </label>
                  ))}
                </div>
                <button data-testid="portal-accept-btn" disabled={!acc.terms || !acc.privacy || !acc.rules || accepting} onClick={doAccept}
                  className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl bg-indigo-500 py-3.5 text-sm font-semibold text-white shadow-lg shadow-indigo-500/25 transition-all active:scale-[0.98] disabled:opacity-40">
                  {accepting ? <Loader2 size={17} className="animate-spin" /> : <ArrowRight size={17} />} {accepting ? t.accepting : t.accept}
                </button>
              </Card>
            )}

            {/* STEP: Check-in — Selettore ospiti → Prosegui → Tally mascherato */}
            {showCheckinForm && (
              <Card testid="portal-checkin" className="mt-4">
                <div className="flex items-center gap-2 font-semibold text-slate-100"><FileText size={18} className="text-indigo-300" /> {needChanges ? t.changesTitle : t.checkinTitle}</div>
                <p className="mt-1 text-xs text-slate-400">{needChanges ? t.changesSub : t.checkinSub}</p>

                {/* Quanti ospiti? (replica check-in, dinamico per capienza, max 8) */}
                <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.03] p-4" data-testid="portal-guests">
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-100"><Users size={16} className="text-indigo-300" /> {t.guestsQ}</div>
                  <p className="mt-1 text-xs text-slate-400">{t.guestsHint}</p>
                  <div className="mt-3 grid grid-cols-4 gap-2" data-testid="portal-guest-options">
                    {guestOptions.map((o) => (
                      <button key={o.guests} data-testid={`portal-guest-${o.guests}`} disabled={!o.available}
                        onClick={() => { setGuestSel(o.guests); setGuestsConfirmed(false); }}
                        className={`flex items-center justify-center rounded-xl border py-3 text-sm font-semibold transition-all ${
                          guestSel === o.guests ? "border-transparent bg-indigo-500 text-white shadow-md"
                          : o.available ? "border-white/15 text-slate-200 hover:border-indigo-400/60"
                          : "cursor-not-allowed border-white/5 text-slate-600"}`}>
                        {o.guests}
                      </button>
                    ))}
                  </div>
                  {!guestsConfirmed ? (
                    <button data-testid="portal-guests-proceed" disabled={!guestSel || savingGuests} onClick={saveGuests}
                      className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl bg-indigo-500 py-3.5 text-sm font-semibold text-white shadow-lg shadow-indigo-500/25 transition-all active:scale-[0.98] disabled:opacity-40">
                      {savingGuests ? <Loader2 size={17} className="animate-spin" /> : <ArrowRight size={17} />} {t.proceed}
                    </button>
                  ) : (
                    <div className="mt-3 flex items-center justify-between text-xs" data-testid="portal-guests-confirmed">
                      <span className="inline-flex items-center gap-1.5 text-emerald-300"><CheckCircle2 size={14} /> {t.guestsSaved}: {guestSel}</span>
                      <button onClick={() => setGuestsConfirmed(false)} className="text-indigo-300 underline">{t.changeGuests}</button>
                    </div>
                  )}
                </div>

                {/* Modulo documenti (visibile solo dopo la scelta ospiti) */}
                {guestsConfirmed && (
                  <>
                    {effTally ? (
                      <a data-testid="portal-open-form-btn" href={`${effTally}${effTally.includes("?") ? "&" : "?"}lang=${lang}`} target="_blank" rel="noreferrer"
                        className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl bg-indigo-500 py-3.5 text-sm font-semibold text-white shadow-lg shadow-indigo-500/25 transition-all active:scale-[0.98]">
                        <ExternalLink size={17} /> {t.openForm}
                      </a>
                    ) : null}
                    {tallySrc ? (
                      <>
                        <p className="mt-3 flex items-center gap-1.5 text-[11px] text-slate-500"><FileText size={12} /> {t.embedNote}</p>
                        <div className="mt-1.5 overflow-hidden rounded-2xl border border-white/10 bg-white">
                          <iframe data-testid="portal-tally-iframe" title="Check-in" src={tallySrc} className="h-[520px] w-full" style={{ border: 0 }} loading="lazy" allow="clipboard-write" />
                        </div>
                      </>
                    ) : null}
                    {effTally ? (
                      <a data-testid="portal-tally-fallback" href={`${effTally}${effTally.includes("?") ? "&" : "?"}lang=${lang}`} target="_blank" rel="noreferrer" className="mt-2 flex items-center justify-center gap-1.5 text-xs text-indigo-300 underline">
                        <ExternalLink size={13} /> {t.newTab}
                      </a>
                    ) : null}
                    <button data-testid="portal-checkin-done" disabled={submittingDone} onClick={checkinDone} className="mt-3 flex w-full items-center justify-center gap-2 rounded-2xl border border-white/15 bg-white/5 py-3 text-sm font-medium text-slate-200 transition-colors hover:bg-white/10 disabled:opacity-40">
                      {submittingDone ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />} {t.done}
                    </button>
                  </>
                )}
              </Card>
            )}

            {/* STEP: Documenti in verifica */}
            {inReview && (
              <Card testid="portal-review" className="mt-4 border-amber-400/25 bg-amber-500/[0.07]">
                <div className="flex items-center gap-2 font-semibold text-amber-200"><Loader2 size={17} className="animate-spin" /> {t.reviewTitle}</div>
                <p className="mt-1 text-xs text-amber-100/70">{t.reviewSub}</p>
              </Card>
            )}

            {/* STEP: Tassa di soggiorno */}
            {taxBanner()}
            {showTax && (
              <Card testid="portal-tax" className="mt-4 border-indigo-400/25">
                <div className="flex items-center gap-2 font-semibold text-slate-100"><CreditCard size={18} className="text-indigo-300" /> {t.taxTitle}</div>
                {tax.status === "not_calculated" ? (
                  <p className="mt-2 flex items-center gap-2 text-sm text-slate-400"><Loader2 size={15} className="animate-spin" /> {t.taxCalc}</p>
                ) : (
                  <>
                    <p className="mt-1 text-xs text-slate-400">{t.taxSub}</p>
                    <div className="mt-3 flex items-center justify-between rounded-2xl bg-white/[0.04] px-4 py-3">
                      <span className="text-sm text-slate-400">{t.taxAmount}{tax.comune ? ` · ${tax.comune}` : ""}</span>
                      <span className="text-xl font-bold text-indigo-200" data-testid="portal-tax-amount">{eur(tax.amount_to_pay)}</span>
                    </div>
                    {tax.status === "payment_failed" && <p className="mt-2 text-xs text-rose-300">{t.taxFailed}</p>}
                    {tax.status === "payment_pending" ? (
                      <div className="mt-3 flex items-center justify-center gap-2 rounded-2xl border border-amber-400/25 bg-amber-500/10 py-3 text-sm text-amber-200"><Loader2 size={15} className="animate-spin" /> {t.taxPending}</div>
                    ) : (
                      <button data-testid="portal-pay-tax-btn" disabled={paying} onClick={payTax}
                        className="mt-3 flex w-full items-center justify-center gap-2 rounded-2xl bg-indigo-500 py-3.5 text-sm font-semibold text-white shadow-lg shadow-indigo-500/25 transition-all active:scale-[0.98] disabled:opacity-40">
                        {paying ? <Loader2 size={17} className="animate-spin" /> : <CreditCard size={17} />} {paying ? t.taxPaying : t.taxPay}
                      </button>
                    )}
                  </>
                )}
              </Card>
            )}

            {/* STEP: Check-in ricevuto (docs + tassa completati, in attesa verifica/istruzioni operatore) */}
            {d.checkin_received && !d.access_unlocked && (
              <Card testid="portal-received" className="mt-4 border-emerald-400/25 bg-emerald-500/10">
                <div className="flex items-center gap-2 font-semibold text-emerald-200"><CheckCircle2 size={18} /> {t.receivedTitle}</div>
                <p className="mt-2 text-sm leading-relaxed text-emerald-100/80">{t.receivedSub}</p>
              </Card>
            )}

            {/* STEP: Accesso disponibile → Welcome Center */}
            {d.access_unlocked && (
              <Card testid="portal-access" className="mt-4 border-emerald-400/30 bg-emerald-500/10">
                <div className="flex items-center gap-2 font-semibold text-emerald-200"><DoorOpen size={18} /> {t.accessTitle}</div>
                <p className="mt-1 text-xs text-emerald-100/70">{t.accessSub}</p>
                {d.guide_url && (
                  <a data-testid="portal-welcome-btn" href={`${d.guide_url}${d.guide_url.includes("?") ? "&" : "?"}lang=${lang}`} className="mt-3 flex w-full items-center justify-center gap-2 rounded-2xl bg-emerald-500 py-3.5 text-sm font-semibold text-white shadow-lg shadow-emerald-500/25 transition-all active:scale-[0.98]">
                    <ArrowRight size={17} /> {t.openWelcome}
                  </a>
                )}
              </Card>
            )}
          </>
        )}

        <p className="pt-8 text-center text-[11px] text-slate-600">{t.footer}</p>
      </div>
    </div>
  );
}
